/**
 * Компакт-кадр алерта (G1, ADR 0009): CBOR с ЧИСЛОВЫМИ ключами, цель ≤200 байт
 * (влезает в LoRa/QR/BLE). headline/instruction — по ID из venue.json (в кадре
 * только msg-id), Ed25519 sig 64 Б поверх кадра без поля sig.
 * Маппинг полей — docs/core/EMERGENCY.md §Кадр.
 */

/** Номера полей CBOR-карты (менять НЕЛЬЗЯ — формат напечатанных QR). */
export const F = {
  v: 0,
  venue: 1,
  id: 2,
  seq: 3,
  mode: 4, // 0=EVACUATION 1=SHELTER 2=DRILL
  keyId: 5,
  issued: 6, // unixtime, секунды
  expires: 7,
  blocksNodes: 8, // массив строк-id
  blocksEdges: 9,
  smokeNodes: 10,
  msgId: 11, // id пары headline/instruction в venue.evacuation.messages
  sig: 15,
} as const

export type EmergencyModeNum = 0 | 1 | 2
export const MODE_NAMES = ['EVACUATION', 'SHELTER', 'DRILL'] as const

export interface EmergencyFrame {
  readonly v: number
  readonly venue: string
  readonly id: string
  readonly seq: number
  readonly mode: EmergencyModeNum
  readonly keyId: string
  readonly issued: number
  readonly expires: number
  readonly blocksNodes: readonly string[]
  readonly blocksEdges: readonly string[]
  readonly smokeNodes: readonly string[]
  readonly msgId: string | null
  readonly sig: Uint8Array
}

// ── мини-CBOR (подмножество: uint, tstr, bstr, array, map) — без зависимостей ──

function pushHead(out: number[], major: number, value: number): void {
  if (value < 24) out.push((major << 5) | value)
  else if (value < 256) out.push((major << 5) | 24, value)
  else out.push((major << 5) | 25, value >> 8, value & 0xff)
}

function pushText(out: number[], text: string): void {
  const bytes = new TextEncoder().encode(text)
  pushHead(out, 3, bytes.length)
  out.push(...bytes)
}

function pushStrArray(out: number[], items: readonly string[]): void {
  pushHead(out, 4, items.length)
  for (const item of items) pushText(out, item)
}

/** Кодирование кадра; withSig=false — байты «к подписи». */
export function encodeFrame(frame: Omit<EmergencyFrame, 'sig'>, sig?: Uint8Array): Uint8Array {
  const out: number[] = []
  const entries: Array<[number, () => void]> = [
    [F.v, () => pushHead(out, 0, frame.v)],
    [F.venue, () => pushText(out, frame.venue)],
    [F.id, () => pushText(out, frame.id)],
    [F.seq, () => pushHead(out, 0, frame.seq)],
    [F.mode, () => pushHead(out, 0, frame.mode)],
    [F.keyId, () => pushText(out, frame.keyId)],
    [F.issued, () => pushUint32(out, frame.issued)],
    [F.expires, () => pushUint32(out, frame.expires)],
  ]
  if (frame.blocksNodes.length)
    entries.push([F.blocksNodes, () => pushStrArray(out, frame.blocksNodes)])
  if (frame.blocksEdges.length)
    entries.push([F.blocksEdges, () => pushStrArray(out, frame.blocksEdges)])
  if (frame.smokeNodes.length)
    entries.push([F.smokeNodes, () => pushStrArray(out, frame.smokeNodes)])
  const msgId = frame.msgId
  if (msgId) entries.push([F.msgId, () => pushText(out, msgId)])
  if (sig)
    entries.push([
      F.sig,
      () => {
        pushHead(out, 2, sig.length)
        out.push(...sig)
      },
    ])
  pushHead(out, 5, entries.length)
  for (const [key, write] of entries) {
    pushHead(out, 0, key)
    write()
  }
  return new Uint8Array(out)
}

function pushUint32(out: number[], value: number): void {
  out.push(
    (0 << 5) | 26,
    (value >>> 24) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 8) & 0xff,
    value & 0xff,
  )
}

class Reader {
  private pos = 0
  private readonly buf: Uint8Array
  constructor(buf: Uint8Array) {
    this.buf = buf
  }
  private byte(): number {
    const b = this.buf[this.pos]
    if (b === undefined) throw new Error('cbor: eof')
    this.pos++
    return b
  }
  head(): { major: number; value: number } {
    const first = this.byte()
    const major = first >> 5
    const info = first & 0x1f
    if (info < 24) return { major, value: info }
    if (info === 24) return { major, value: this.byte() }
    if (info === 25) return { major, value: (this.byte() << 8) | this.byte() }
    if (info === 26) {
      return {
        major,
        value: ((this.byte() << 24) | (this.byte() << 16) | (this.byte() << 8) | this.byte()) >>> 0,
      }
    }
    throw new Error('cbor: unsupported length')
  }
  bytes(len: number): Uint8Array {
    const slice = this.buf.slice(this.pos, this.pos + len)
    if (slice.length !== len) throw new Error('cbor: eof')
    this.pos += len
    return slice
  }
  text(len: number): string {
    return new TextDecoder().decode(this.bytes(len))
  }
}

function readValue(r: Reader): unknown {
  const value = r.head()
  if (value.major === 0) return value.value
  if (value.major === 2) return r.bytes(value.value)
  if (value.major === 3) return r.text(value.value)
  if (value.major === 4) {
    const items: string[] = []
    for (let j = 0; j < value.value; j++) {
      const el = r.head()
      if (el.major !== 3) throw new Error('cbor: tstr expected')
      items.push(r.text(el.value))
    }
    return items
  }
  throw new Error('cbor: unsupported major')
}

function readMap(buf: Uint8Array): Record<number, unknown> {
  const r = new Reader(buf)
  const map = r.head()
  if (map.major !== 5) throw new Error('cbor: map expected')
  const out: Record<number, unknown> = {}
  for (let i = 0; i < map.value; i++) {
    const key = r.head()
    if (key.major !== 0) throw new Error('cbor: int key expected')
    out[key.value] = readValue(r)
  }
  return out
}

/** Разбор кадра; бросает при структурном мусоре (verify снаружи молча игнорит). */
export function decodeFrame(buf: Uint8Array): EmergencyFrame {
  const out = readMap(buf)
  const need = (k: number): unknown => {
    if (out[k] === undefined) throw new Error(`frame: field ${k} missing`)
    return out[k]
  }
  const mode = need(F.mode) as number
  if (mode !== 0 && mode !== 1 && mode !== 2) throw new Error('frame: bad mode')
  return {
    v: need(F.v) as number,
    venue: need(F.venue) as string,
    id: need(F.id) as string,
    seq: need(F.seq) as number,
    mode,
    keyId: need(F.keyId) as string,
    issued: need(F.issued) as number,
    expires: need(F.expires) as number,
    blocksNodes: (out[F.blocksNodes] as string[]) ?? [],
    blocksEdges: (out[F.blocksEdges] as string[]) ?? [],
    smokeNodes: (out[F.smokeNodes] as string[]) ?? [],
    msgId: (out[F.msgId] as string) ?? null,
    sig: (out[F.sig] as Uint8Array) ?? new Uint8Array(),
  }
}

/** base64url кадра для ?e= (ADR 0004: контракт URL не меняется). */
export function frameToParam(bytes: Uint8Array): string {
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '')
}

export function paramToFrame(param: string): Uint8Array {
  const b64 = param.replaceAll('-', '+').replaceAll('_', '/')
  const bin = atob(b64)
  return Uint8Array.from(bin, (c) => c.charCodeAt(0))
}
